import React from "react";
import { Card, Form, Row, Col, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRefresh, faCalendarAlt, faUsers, faBullseye } from "@fortawesome/free-solid-svg-icons";
import { MetaResumida } from "../../../../types/meta";

interface MetaSelectorProps {
  selectedMeta: MetaResumida | null;
  metasDisponiveis: MetaResumida[];
  loading: boolean;
  onMetaChange: (meta: MetaResumida) => void;
  onRefresh: () => void;
}

export const MetaSelector: React.FC<MetaSelectorProps> = ({
  selectedMeta,
  metasDisponiveis,
  loading,
  onMetaChange,
  onRefresh,
}) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("pt-BR");
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const getMetaTotal = (meta: MetaResumida) => {
    return meta.usuarios.reduce((total, usuario) => total + usuario.valorMeta, 0);
  };

  return (
    <Card className="border-0 shadow-sm mb-4">
      <Card.Header className="border-0 py-3">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{
                width: "40px",
                height: "40px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
              }}
            >
              <FontAwesomeIcon icon={faBullseye} />
            </div>
            <div>
              <h6 className="mb-0 fw-bold">Seleção de Meta</h6>
              <small className="text-muted">Escolha a meta para visualizar o dashboard</small>
            </div>
          </div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={onRefresh}
            disabled={loading}
          >
            <FontAwesomeIcon
              icon={faRefresh}
              spin={loading}
              className="me-1"
            />
            Atualizar
          </Button>
        </div>
      </Card.Header>

      <Card.Body>
        <Row className="align-items-center">
          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-semibold">Meta Selecionada</Form.Label>
              <Form.Select
                value={selectedMeta?.id || ""}
                onChange={(e) => {
                  const metaId = parseInt(e.target.value);
                  const meta = metasDisponiveis.find(m => m.id === metaId);
                  if (meta) onMetaChange(meta);
                }}
                disabled={loading}
              >
                <option value="">Selecione uma meta...</option>
                {metasDisponiveis.map((meta) => (
                  <option key={meta.id} value={meta.id}>
                    {meta.descricao || `Meta ${meta.id}`} - {meta.usuarios.length} colaborador{meta.usuarios.length !== 1 ? 'es' : ''}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          {selectedMeta && (
            <Col md={6}>
              <div className="d-flex flex-wrap gap-3">
                <div className="text-center">
                  <div className="d-flex align-items-center text-muted mb-1">
                    <FontAwesomeIcon icon={faCalendarAlt} className="me-1" size="sm" />
                    <small>Período</small>
                  </div>
                  <div className="fw-semibold" style={{ fontSize: "14px" }}>
                    {formatDate(selectedMeta.dataInicio?.split("T")[0])} - {formatDate(selectedMeta.dataFim?.split("T")[0])}
                  </div>
                </div>

                <div className="text-center">
                  <div className="d-flex align-items-center text-muted mb-1">
                    <FontAwesomeIcon icon={faUsers} className="me-1" size="sm" />
                    <small>Colaboradores</small>
                  </div>
                  <div className="fw-semibold" style={{ fontSize: "14px" }}>
                    {selectedMeta.usuarios.length}
                  </div>
                </div>

                <div className="text-center">
                  <div className="d-flex align-items-center text-muted mb-1">
                    <FontAwesomeIcon icon={faBullseye} className="me-1" size="sm" />
                    <small>Meta Total</small>
                  </div>
                  <div className="fw-semibold text-primary" style={{ fontSize: "14px" }}>
                    {formatCurrency(getMetaTotal(selectedMeta))}
                  </div>
                </div>
              </div>
            </Col>
          )}
        </Row>

        {!selectedMeta && metasDisponiveis.length === 0 && !loading && (
          <div className="text-center text-muted py-3">
            <FontAwesomeIcon icon={faBullseye} size="2x" className="mb-2" />
            <p>Nenhuma meta ativa encontrada</p>
            <small>Crie uma nova meta na aba "Gerenciamento" para visualizar o dashboard</small>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};
